import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { videosAPI } from '../services/api';
import VideoCard from '../components/VideoCard';
import { buildSides } from '../utils/battleSides';

// Matches BattleVoting's sideLabel treatment: "A & B" for a 2v2 side,
// just "A" for a 1v1 side.
const sideLabel = (side) =>
  side.artists.map((a) => a.display_name || a.username).join(' & ');

const SOURCE_OPTIONS = [
  { key: '', label: 'All' },
  { key: 'youtube', label: 'YouTube' },
  { key: 'mux', label: 'Exclusive' },
];

/**
 * Hero skeleton placeholder shown during loading.
 * Full-bleed shape matching the cinematic hero below.
 */
const HeroSkeleton = () => (
  <section data-testid="hero-skeleton" className="hero-section">
    {/* Full-bleed image placeholder */}
    <div className="absolute inset-0 bg-battle-gray-light">
      <div className="skeleton-shimmer absolute inset-0" />
    </div>
    {/* Bottom scrim area with stacked text skeletons */}
    <div
      className="absolute inset-0"
      style={{
        background: 'linear-gradient(to top, rgba(10,11,8,0.98) 0%, rgba(10,11,8,0.70) 45%, transparent 80%)',
      }}
    />
    <div className="absolute bottom-0 left-0 right-0 px-6 sm:px-10 pb-10 md:pb-14 max-w-3xl">
      <div className="h-4 w-28 rounded-full bg-battle-gray-light relative overflow-hidden mb-4">
        <div className="skeleton-shimmer absolute inset-0" />
      </div>
      <div className="h-10 w-72 rounded bg-battle-gray-light relative overflow-hidden mb-3">
        <div className="skeleton-shimmer absolute inset-0" />
      </div>
      <div className="h-5 w-16 rounded bg-battle-gray-light relative overflow-hidden mb-4">
        <div className="skeleton-shimmer absolute inset-0" />
      </div>
      <div className="h-4 w-44 rounded bg-battle-gray-light relative overflow-hidden mb-6">
        <div className="skeleton-shimmer absolute inset-0" />
      </div>
      <div className="h-12 w-36 rounded-lg bg-battle-gray-light relative overflow-hidden">
        <div className="skeleton-shimmer absolute inset-0" />
      </div>
    </div>
  </section>
);

/**
 * Content row skeleton shown during loading
 */
const ContentRowSkeleton = ({ testId }) => (
  <section data-testid={testId} className="mb-12">
    <div className="flex items-center justify-between mb-5 px-4 sm:px-6 max-w-7xl mx-auto">
      <div className="h-6 w-36 rounded bg-battle-gray-light relative overflow-hidden">
        <div className="skeleton-shimmer absolute inset-0" />
      </div>
    </div>
    <div className="flex gap-4 overflow-hidden px-4 sm:px-6 pb-4">
      {[...Array(5)].map((_, i) => (
        <div
          key={i}
          className="flex-shrink-0 w-64 md:w-72 lg:w-80 rounded-xl bg-battle-gray border border-gray-800/60 overflow-hidden"
          style={{ animationDelay: `${i * 0.1}s` }}
        >
          <div className="aspect-video bg-battle-gray-light relative overflow-hidden">
            <div className="skeleton-shimmer absolute inset-0" />
          </div>
          <div className="p-4 space-y-3">
            <div className="h-4 bg-battle-gray-light rounded w-3/4 relative overflow-hidden">
              <div className="skeleton-shimmer absolute inset-0" />
            </div>
            <div className="h-3 bg-battle-gray-light rounded w-1/2 relative overflow-hidden">
              <div className="skeleton-shimmer absolute inset-0" />
            </div>
          </div>
        </div>
      ))}
    </div>
  </section>
);

/**
 * Horizontal scroll row for Trending / Just Dropped
 */
const ContentRow = ({ title, testId, videos }) => {
  const scrollRef = useRef(null);

  const scroll = (dir) => {
    if (scrollRef.current) {
      const amount = scrollRef.current.offsetWidth * 0.75;
      scrollRef.current.scrollBy({ left: dir === 'left' ? -amount : amount, behavior: 'smooth' });
    }
  };

  if (!videos || videos.length === 0) return null;

  return (
    <section data-testid={testId} className="mb-12">
      {/* Section header with red bar accent */}
      <div className="flex items-center justify-between mb-5 px-4 sm:px-6 max-w-7xl mx-auto">
        <div className="row-section-header">
          <h2 className="font-display text-xl md:text-2xl font-black uppercase tracking-tight text-white">
            {title}
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => scroll('left')}
            className="w-8 h-8 rounded-full border border-army/50 hover:border-brand-yellow/60
                       text-gray-500 hover:text-white flex items-center justify-center
                       transition-all duration-200 hover:bg-battle-gray-light"
            aria-label={`Scroll ${title} left`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <button
            onClick={() => scroll('right')}
            className="w-8 h-8 rounded-full border border-army/50 hover:border-brand-yellow/60
                       text-gray-500 hover:text-white flex items-center justify-center
                       transition-all duration-200 hover:bg-battle-gray-light"
            aria-label={`Scroll ${title} right`}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>

      {/* Scrollable row with right-edge fade mask */}
      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto px-4 sm:px-6 pb-4 scrollbar-hide snap-x snap-mandatory row-mask-right"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {videos.map((video) => (
            <div key={video.id} className="flex-shrink-0 w-64 md:w-72 lg:w-80 snap-start">
              <VideoCard video={video} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const Browse = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [featuredVideos, setFeaturedVideos] = useState([]);
  const [trendingVideos, setTrendingVideos] = useState([]);
  const [recentVideos, setRecentVideos] = useState([]);
  const [allVideos, setAllVideos] = useState([]);

  const [featuredLoading, setFeaturedLoading] = useState(true);
  const [trendingLoading, setTrendingLoading] = useState(true);
  const [recentLoading, setRecentLoading] = useState(true);
  const [allLoading, setAllLoading] = useState(true);

  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [filters, setFilters] = useState({
    search: searchParams.get('search') || '',
    artist_id: searchParams.get('artist_id') || '',
    platform: searchParams.get('platform') || '',
  });

  useEffect(() => {
    fetchFeatured();
    fetchTrending();
    fetchRecent();
  }, []);

  useEffect(() => {
    fetchAllVideos();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchFeatured = async () => {
    setFeaturedLoading(true);
    try {
      const response = await videosAPI.getFeatured();
      setFeaturedVideos(response.data);
    } catch (error) {
      console.error('Error fetching featured:', error);
    } finally {
      setFeaturedLoading(false);
    }
  };

  const fetchTrending = async () => {
    setTrendingLoading(true);
    try {
      const response = await videosAPI.getTrending();
      setTrendingVideos(response.data);
    } catch (error) {
      console.error('Error fetching trending:', error);
    } finally {
      setTrendingLoading(false);
    }
  };

  const fetchRecent = async () => {
    setRecentLoading(true);
    try {
      const response = await videosAPI.getRecent();
      setRecentVideos(response.data);
    } catch (error) {
      console.error('Error fetching recent:', error);
    } finally {
      setRecentLoading(false);
    }
  };

  const fetchAllVideos = async () => {
    setAllLoading(true);
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.artist_id) params.artist_id = filters.artist_id;
      if (filters.platform) params.platform = filters.platform;

      const response = await videosAPI.getAll(params);
      setAllVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setAllLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);

    const params = new URLSearchParams();
    Object.keys(newFilters).forEach((k) => {
      if (newFilters[k]) params.set(k, newFilters[k]);
    });
    setSearchParams(params);
  };

  const clearFilters = () => {
    setFilters({ search: '', artist_id: '', platform: '' });
    setSearchParams({});
  };

  const hasActiveFilters = filters.search || filters.artist_id || filters.platform;

  const displayFeatured = featuredVideos.length > 0 ? featuredVideos : [];

  useEffect(() => {
    if (displayFeatured.length <= 1) return;
    const interval = setInterval(() => {
      setFeaturedIndex((prev) => (prev + 1) % displayFeatured.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [displayFeatured.length]);

  const currentFeatured = displayFeatured[featuredIndex] || displayFeatured[0];
  const featuredArtists = currentFeatured?.artists || [];
  // Groups featuredArtists by side (1v1 = one per side, 2v2 = two per side)
  // instead of indexing featuredArtists[0]/[1] directly, which silently
  // dropped half the roster on a 2v2 battle.
  const featuredSides = buildSides(featuredArtists);

  return (
    <div className="min-h-screen">

      {/* ====== 1. FEATURED BATTLE HERO ====== */}
      {featuredLoading && <HeroSkeleton />}
      {!featuredLoading && displayFeatured.length > 0 && currentFeatured && (
        <section data-testid="featured-hero" className="hero-section group/hero">
          {/* Full-bleed backdrop: the battle thumbnail IS the background */}
          <div className="absolute inset-0">
            {currentFeatured.thumbnail_url ? (
              <img
                src={currentFeatured.thumbnail_url}
                alt=""
                aria-hidden="true"
                className="w-full h-full object-cover group-hover/hero:scale-[1.03] transition-transform duration-700 ease-out"
              />
            ) : (
              <div className="w-full h-full bg-battle-gray" />
            )}
          </div>

          {/* Gradient scrims: bottom-up primary + left-edge darkening */}
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to top, rgba(10,11,8,1) 0%, rgba(10,11,8,0.80) 38%, rgba(10,11,8,0.30) 65%, transparent 100%)',
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                'linear-gradient(to right, rgba(10,11,8,0.85) 0%, rgba(10,11,8,0.50) 40%, transparent 70%)',
            }}
          />

          {/* Content: overlaid in lower-left, max two-thirds width */}
          <div className="absolute bottom-0 left-0 right-0 px-6 sm:px-10 pb-10 md:pb-14">
            <div className="max-w-2xl">
              {/* "Featured Battle" pill */}
              <div
                className="inline-flex items-center gap-2 mb-4 px-3 py-1 rounded-full text-brand-yellow text-xs font-bold tracking-widest uppercase"
                style={{
                  border: '1px solid rgba(245,230,66,0.35)',
                  background: 'rgba(245,230,66,0.08)',
                }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full bg-brand-yellow"
                  style={{ boxShadow: '0 0 6px rgba(245,230,66,0.75)' }}
                />
                Featured Battle
              </div>

              {/* Artist VS treatment */}
              {featuredArtists.length >= 2 ? (
                <div className="mb-3">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="font-display font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-none drop-shadow-lg">
                      {sideLabel(featuredSides[0])}
                    </span>
                    <span
                      className="font-black text-lg md:text-xl px-3 py-1 rounded text-brand-yellow"
                      style={{
                        border: '1px solid rgba(245,230,66,0.45)',
                        background: 'rgba(245,230,66,0.12)',
                      }}
                    >
                      VS
                    </span>
                    <span className="font-display font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight text-white leading-none drop-shadow-lg">
                      {sideLabel(featuredSides[1])}
                    </span>
                  </div>
                </div>
              ) : (
                <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight mb-3 text-white leading-none drop-shadow-lg">
                  {currentFeatured.title}
                </h2>
              )}

              {currentFeatured.event_name && (
                <p className="text-gray-300 text-sm mb-6 uppercase tracking-widest font-medium">
                  {currentFeatured.event_name}
                </p>
              )}

              {/* CTA + dot navigation row */}
              <div className="flex items-center gap-6 flex-wrap">
                <Link
                  to={`/video/${currentFeatured.id}`}
                  className="btn-primary inline-flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Watch Now
                </Link>

                {/* Dot navigation */}
                {displayFeatured.length > 1 && (
                  <div className="flex gap-2">
                    {displayFeatured.map((_, i) => (
                      <button
                        key={i}
                        onClick={() => setFeaturedIndex(i)}
                        className={i === featuredIndex ? 'dot-active' : 'dot-inactive'}
                        aria-label={`Show featured battle ${i + 1}`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ====== 2. ALL BATTLES HEADING ====== */}
      <section className="pt-8 pb-4 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <h1 className="section-title text-white text-3xl md:text-4xl mb-2" data-testid="browse-title">
            ALL <span className="text-brand-yellow">BATTLES</span>
          </h1>
          <p className="text-gray-600 text-sm">
            The complete catalog. Every battle. Every bar.
            {!allLoading && allVideos.length > 0 && (
              <span className="text-gray-700 ml-2">({allVideos.length} battles)</span>
            )}
          </p>
        </div>
      </section>

      {/* ====== 3. STICKY FILTER BAR ====== */}
      <div
        className="sticky top-16 z-40 border-y px-4 sm:px-6 py-4"
        style={{
          background: 'rgba(10,11,8,0.94)',
          backdropFilter: 'blur(16px)',
          borderColor: 'rgba(255,255,255,0.07)',
        }}
      >
        <div className="max-w-7xl mx-auto space-y-3">

          {/* Search with icon */}
          <div className="relative">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-600 pointer-events-none"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
            </svg>
            <input
              type="text"
              placeholder="Search battles, artists..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              className="search-field"
              data-testid="search-input"
            />
          </div>

          {/* Source pill filters */}
          <div className="flex flex-wrap items-center gap-2" data-testid="filter-pills">
            <span className="text-gray-600 text-xs font-bold uppercase tracking-widest mr-1">Source</span>
            {SOURCE_OPTIONS.map((opt) => (
              <button
                key={opt.key || 'all'}
                onClick={() => handleFilterChange('platform', opt.key)}
                data-testid={`pill-${opt.key || 'all'}`}
                className={`filter-pill ${
                  filters.platform === opt.key
                    ? 'filter-pill-active bg-brand-yellow border-brand-yellow'
                    : 'filter-pill-inactive'
                }`}
              >
                {opt.label}
              </button>
            ))}

            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="ml-2 text-gray-500 hover:text-brand-yellow text-xs font-semibold transition-colors flex items-center gap-1"
                data-testid="clear-filters-btn"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ====== 4. ALL BATTLES GRID ====== */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {allLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {[...Array(8)].map((_, i) => (
              <div
                key={i}
                data-testid="skeleton-card"
                className="card overflow-hidden rounded-xl"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="aspect-video bg-battle-gray-light relative overflow-hidden">
                  <div className="skeleton-shimmer absolute inset-0" />
                </div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-battle-gray-light rounded w-3/4 relative overflow-hidden">
                    <div className="skeleton-shimmer absolute inset-0" />
                  </div>
                  <div className="h-3 bg-battle-gray-light rounded w-1/2 relative overflow-hidden">
                    <div className="skeleton-shimmer absolute inset-0" />
                  </div>
                  <div className="flex justify-between">
                    <div className="h-3 bg-battle-gray-light rounded w-1/4 relative overflow-hidden">
                      <div className="skeleton-shimmer absolute inset-0" />
                    </div>
                    <div className="h-3 bg-battle-gray-light rounded w-1/6 relative overflow-hidden">
                      <div className="skeleton-shimmer absolute inset-0" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : allVideos.length > 0 ? (
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 fade-in-content"
            data-testid="videos-grid"
          >
            {allVideos.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="text-center py-24">
            <div
              className="w-20 h-20 mx-auto mb-6 rounded-full flex items-center justify-center"
              style={{
                background: '#141414',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <svg className="w-9 h-9 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z" />
              </svg>
            </div>
            <p className="text-white text-xl font-black font-display uppercase tracking-tight mb-2">No battles found</p>
            <p className="text-gray-600 text-sm max-w-md mx-auto">
              {hasActiveFilters
                ? 'Try adjusting your filters to find what you\'re looking for.'
                : 'Check back soon for new battles dropping.'}
            </p>
            {hasActiveFilters && (
              <button
                onClick={clearFilters}
                className="mt-6 btn-secondary py-2 px-5 text-sm"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* ====== 5. TRENDING ROW ====== */}
      {trendingLoading && (
        <div className="pb-2">
          <ContentRowSkeleton testId="content-row-skeleton-1" />
        </div>
      )}
      {!trendingLoading && trendingVideos.length > 0 && (
        <div className="fade-in-content">
          <ContentRow
            title="Trending"
            testId="section-trending"
            videos={trendingVideos}
          />
        </div>
      )}

      {/* ====== 6. JUST DROPPED ROW ====== */}
      {recentLoading && (
        <div className="pb-8">
          <ContentRowSkeleton testId="content-row-skeleton-2" />
        </div>
      )}
      {!recentLoading && recentVideos.length > 0 && (
        <div className="pb-8 fade-in-content">
          <ContentRow
            title="Just Dropped"
            testId="section-just-dropped"
            videos={recentVideos}
          />
        </div>
      )}

    </div>
  );
};

export default Browse;
